// frontend/src/pages/Login.jsx
import { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from '../utils/auth';
import { totpApi, passkeysApi } from '../utils/api';
import toast from 'react-hot-toast';
import TotpChallengeForm from '../components/TotpChallengeForm';
import TotpEnrollForm from '../components/TotpEnrollForm';
import TotpBackupCodes from '../components/TotpBackupCodes';
import BrandIcon from '../components/BrandIcon';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const { login, loginWithPasskey, completeTotpLogin, completeTotpSetup } = useAuth();

  // step: 'email' | 'password' | 'totp' | 'setup-qr' | 'setup-codes'
  // Connexion en deux temps (email puis mot de passe, façon Google) plutôt
  // qu'un seul formulaire — l'appel /auth/login n'a lieu qu'à l'étape 'password'.
  const [step, setStep] = useState('email');
  const [challengeToken, setChallengeToken] = useState(null);
  const [totpError, setTotpError] = useState(null);

  // Enrôlement forcé par l'organisation
  const [enrollData, setEnrollData] = useState(null); // { secret, qrDataUrl }
  const [enrollRemember, setEnrollRemember] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  // Étape 1 : juste passer à l'écran mot de passe, pas d'appel réseau
  // (POST /auth/login attend email + password ensemble).
  const submitEmail = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStep('password');
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.requiresTotp) {
        setChallengeToken(data.challengeToken);
        setStep('totp');
      } else if (data?.requiresTotpSetup) {
        setChallengeToken(data.challengeToken);
        const r = await totpApi.setupChallenge({ challengeToken: data.challengeToken });
        setEnrollData(r.data);
        setStep('setup-qr');
      }
      // sinon login() a déjà redirigé vers /app
    } catch {
      toast.error('Email ou mot de passe incorrect');
    } finally { setLoading(false); }
  };

  const handleTotpSubmit = async (code, isBackupCode, remember) => {
    setTotpError(null);
    setLoading(true);
    try {
      await completeTotpLogin(challengeToken, code, isBackupCode, remember);
      // redirige vers /app
    } catch (err) {
      setTotpError(err.response?.data?.error || 'Code invalide');
    } finally { setLoading(false); }
  };

  const handleEnrollVerify = async (code) => {
    setTotpError(null);
    setLoading(true);
    try {
      const r = await completeTotpSetup(challengeToken, enrollData.secret, code, enrollRemember);
      setBackupCodes(r.backupCodes || []);
      setStep('setup-codes');
    } catch (err) {
      setTotpError(err.response?.data?.error || 'Code invalide');
    } finally { setLoading(false); }
  };

  // Connexion sans mot de passe via clé d'accès (WebAuthn "usernameless") :
  // on ne demande pas d'email au préalable, le navigateur propose directement
  // les clés enregistrées pour ce domaine.
  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const { data: options } = await passkeysApi.loginOptions();
      const response = await startAuthentication(options);
      const { data } = await passkeysApi.loginVerify(response);
      loginWithPasskey(data.token);
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        // Annulé par l'utilisateur (ex: fermeture de la boîte de dialogue) — silencieux.
      } else {
        toast.error(err.response?.data?.error || "Échec de la connexion par clé d'accès");
      }
    } finally { setPasskeyLoading(false); }
  };

  const backToLogin = () => {
    setStep('email'); setPassword(''); setChallengeToken(null); setTotpError(null);
    setEnrollData(null); setBackupCodes(null);
  };

  if (step === 'totp') {
    return (
      <TotpChallengeForm
        onSubmit={handleTotpSubmit}
        onBack={backToLogin}
        loading={loading}
        error={totpError}
      />
    );
  }

  if (step === 'setup-qr') {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
        background:'#0a0b0f', fontFamily:'Inter, sans-serif', padding:20 }}>
        <div style={{ width:400, maxWidth:'100%' }}>
          <div style={{ textAlign:'center', marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }}><BrandIcon size={44}/></div>
            <h1 style={{ color:'#fff', fontSize:20, fontWeight:700, margin:0 }}>
              Configuration de la double authentification
            </h1>
            <p style={{ color:'#888', fontSize:13, marginTop:8 }}>
              Votre organisation exige la double authentification
            </p>
          </div>
          {enrollData && (
            <TotpEnrollForm
              qrDataUrl={enrollData.qrDataUrl}
              secret={enrollData.secret}
              onVerify={handleEnrollVerify}
              loading={loading}
              error={totpError}
              showRemember
              remember={enrollRemember}
              setRemember={setEnrollRemember}
              dark
            />
          )}
          <button type="button" onClick={backToLogin}
            style={{ display:'block', margin:'16px auto 0', background:'none', border:'none',
              color:'#888', textDecoration:'underline', cursor:'pointer', fontSize:13 }}>
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  if (step === 'setup-codes') {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
        background:'#0a0b0f', fontFamily:'Inter, sans-serif', padding:20 }}>
        <div style={{ width:440, maxWidth:'100%' }}>
          <div style={{ textAlign:'center', marginBottom:16 }}>
            <div style={{ fontSize:40, marginBottom:12 }}>✓</div>
            <h1 style={{ color:'#fff', fontSize:20, fontWeight:700, margin:0 }}>2FA activée</h1>
          </div>
          <TotpBackupCodes codes={backupCodes || []} onDone={() => window.location.replace('/app')} dark/>
        </div>
      </div>
    );
  }

  // Étape 1 : email seul
  if (step === 'email') {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-base)' }}>
        <div style={{ width:'100%', maxWidth:400, padding:'0 16px' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }}><BrandIcon size={52}/></div>
            <h1 style={{ fontSize:28, fontWeight:700, background:'linear-gradient(135deg,var(--accent-light),var(--teal))', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              EscapeOS
            </h1>
            <p style={{ color:'var(--text-2)', marginTop:6 }}>Espace de gestion</p>
          </div>
          <form onSubmit={submitEmail} style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <div className="card" style={{ padding:28 }}>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">Email</label>
                <input className="input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="votre@email.fr" autoFocus />
              </div>
            </div>
            <button className="btn btn-primary w-full" style={{justifyContent:'center', padding:'13px'}}>
              Continuer
            </button>
          </form>
          <div style={{ display:'flex', alignItems:'center', gap:12, margin:'20px 0' }}>
            <div style={{ flex:1, height:1, background:'var(--border, #2a2a35)' }} />
            <span style={{ color:'var(--text-3)', fontSize:12 }}>ou</span>
            <div style={{ flex:1, height:1, background:'var(--border, #2a2a35)' }} />
          </div>
          <button type="button" onClick={handlePasskeyLogin} disabled={passkeyLoading}
            className="btn w-full" style={{ justifyContent:'center', padding:'13px' }}>
            {passkeyLoading ? <span className="spinner" style={{width:18,height:18}} /> : 'Se connecter avec une clé d’accès'}
          </button>
          <p style={{ textAlign:'center', marginTop:24, fontSize:13, color:'var(--text-3)' }}>
            <a href="/reserver" style={{ color:'var(--teal)' }}>→ Voir le planning public</a>
          </p>
        </div>
      </div>
    );
  }

  // Étape 2 : mot de passe (email déjà connu, modifiable via "Modifier")
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-base)' }}>
      <div style={{ width:'100%', maxWidth:400, padding:'0 16px' }}>
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:12 }}><BrandIcon size={52}/></div>
          <h1 style={{ fontSize:28, fontWeight:700, background:'linear-gradient(135deg,var(--accent-light),var(--teal))', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
            EscapeOS
          </h1>
        </div>
        <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="card" style={{ padding:28 }}>
            <div className="form-group" style={{ marginBottom:14, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:14, color:'var(--text-2)' }}>{email}</span>
              <button type="button" onClick={() => setStep('email')}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--teal)', fontSize:12, fontWeight:600 }}>
                Modifier
              </button>
            </div>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">Mot de passe</label>
              <input className="input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required placeholder="••••••••" autoFocus />
              <a href="/mot-de-passe-oublie" style={{ display:'block', textAlign:'right', marginTop:6, fontSize:12, color:'var(--text-3)' }}>
                Mot de passe oublié ?
              </a>
            </div>
          </div>
          <button className="btn btn-primary w-full" disabled={loading} style={{justifyContent:'center', padding:'13px'}}>
            {loading ? <span className="spinner" style={{width:18,height:18}} /> : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}
